import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "@/hooks/useTranslation";
import appLogo from "@/assets/eyecontrol-icon-white.png";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, dir } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
  const [email, setEmail] = useState<string>('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      return;
    }

    verifyToken(token);
  }, [searchParams]);

  const verifyToken = async (token: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('custom-auth-email', {
        body: { action: 'verify_token', token }
      });

      if (error) throw error;

      if (data.success) {
        setEmail(data.email);
        setStatus('success');
      } else if (data.error === 'token_expired') {
        setStatus('expired');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
    }
  };

  const getContent = () => {
    switch (status) {
      case 'loading':
        return {
          icon: <Loader2 className="w-16 h-16 text-primary animate-spin" />,
          title: t('verifyingEmail') || 'מאמת את כתובת המייל...',
          description: t('pleaseWait') || 'אנא המתן',
          showButton: false,
        };
      case 'success':
        return {
          icon: <CheckCircle className="w-16 h-16 text-green-500" />,
          title: t('emailVerified') || 'המייל אומת בהצלחה!',
          description: (t('emailVerifiedDesc') || 'כתובת המייל {email} אומתה. כעת ניתן להתחבר.').replace('{email}', email),
          showButton: true,
          buttonText: t('goToLogin') || 'מעבר להתחברות',
        };
      case 'expired':
        return {
          icon: <XCircle className="w-16 h-16 text-orange-500" />,
          title: t('tokenExpired') || 'הקישור פג תוקף',
          description: t('tokenExpiredDesc') || 'קישור האימות פג תוקף. אנא הירשם מחדש.',
          showButton: true,
          buttonText: t('backToSignup') || 'חזרה להרשמה',
        };
      case 'error':
      default:
        return {
          icon: <XCircle className="w-16 h-16 text-destructive" />,
          title: t('verificationFailed') || 'האימות נכשל',
          description: t('verificationFailedDesc') || 'הקישור אינו תקין או שכבר השתמשת בו.',
          showButton: true,
          buttonText: t('backToLogin') || 'חזרה להתחברות',
        };
    }
  };

  const content = getContent();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" dir={dir}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <div className="bg-card border border-border rounded-3xl p-8 shadow-xl text-center">
          {/* Logo */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-primary flex items-center justify-center">
            <img src={appLogo} alt="My EyeControl" className="w-14 h-14" />
          </div>

          {/* Status Icon */}
          <div className="mb-6">
            {content.icon}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {content.title}
          </h1>

          {/* Description */}
          <p className="text-muted-foreground mb-8">
            {content.description}
          </p>

          {/* Button */}
          {content.showButton && (
            <Button
              onClick={() => navigate('/auth')}
              className="w-full h-12 text-lg font-medium"
            >
              {content.buttonText}
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
